import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Droplet, Clock, Heart, Play, Pause, Volume2, VolumeX } from 'lucide-react';

// Liste de vidéos YouTube relaxantes (ID seulement)
const RELAXING_VIDEOS = [
  { id: 'lFcSrYw-ARY', title: 'Musique relaxante avec nature' },
  { id: '1ZYbU82GVz4', title: 'Sons de la forêt' },
  { id: 'eKFTSSKCzWA', title: 'Méditation et détente' },
  { id: 'hlWiI4xVXKY', title: 'Plage tropicale relaxante' },
  { id: '5qap5aO4i9A', title: 'Pluie relaxante' },
];

const PatientView = () => {
  const { infusionId } = useParams();
  const [infusion, setInfusion] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(RELAXING_VIDEOS[0]);
  const [showVideo, setShowVideo] = useState(true);
  const [isMuted, setIsMuted] = useState(false);

  // Charger les données de la perfusion
  useEffect(() => {
    const fetchInfusion = async () => {
      try {
        // Utiliser l'endpoint public qui ne nécessite pas d'authentification
        const response = await axios.get(`${API}/infusions/public/${infusionId}`);
        setInfusion(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching infusion:', err);
        setError('Impossible de charger les données de la perfusion');
      } finally {
        setLoading(false);
      }
    };

    fetchInfusion();
    // Rafraîchir toutes les 10 secondes pour suivre les changements de statut
    const interval = setInterval(fetchInfusion, 10000);
    return () => clearInterval(interval);
  }, [infusionId]);

  // Timer
  useEffect(() => {
    if (infusion && infusion.statut === 'en_cours' && infusion.start_time && !infusion.is_paused) {
      const interval = setInterval(() => {
        const start = new Date(infusion.start_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        setElapsed(diff);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [infusion]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}m ${secs.toString().padStart(2, '0')}s`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getRemainingTime = () => {
    if (!infusion) return 0;
    const totalSeconds = (infusion.protocole?.duree || 45) * 60;
    const remaining = totalSeconds - elapsed;
    return Math.max(0, remaining);
  };

  const getProgress = () => {
    if (!infusion) return 0;
    const totalSeconds = (infusion.protocole?.duree || 45) * 60;
    return Math.min(100, (elapsed / totalSeconds) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !infusion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-center text-white">
          <Heart className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold mb-2">Erreur</h1>
          <p className="text-lg opacity-80">{error || 'Perfusion non trouvée'}</p>
        </div>
      </div>
    );
  }

  if (infusion.statut === 'termine') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-800 via-emerald-800 to-teal-800 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Heart className="w-12 h-12 text-green-300" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Séance terminée !</h1>
          <p className="text-xl opacity-80">Merci d'avoir choisi notre clinique</p>
          <p className="text-lg opacity-60 mt-2">Nous espérons vous revoir bientôt</p>
        </div>
      </div>
    );
  }

  const remainingSeconds = getRemainingTime();
  const progress = getProgress();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 text-white overflow-hidden">
      {/* Header avec info protocole */}
      <div className="absolute top-0 left-0 right-0 bg-black/30 backdrop-blur-sm p-4 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Droplet className="w-5 h-5 text-blue-300" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">{infusion.protocole?.nom || 'Perfusion IV'}</h1>
              <p className="text-sm opacity-70">Durée prévue: {infusion.protocole?.duree || 45} minutes</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setShowVideo(!showVideo)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              {showVideo ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="relative min-h-screen flex flex-col items-center justify-center px-4 pt-20 pb-32">
        {/* Vidéo YouTube en arrière-plan ou intégrée */}
        {showVideo && (
          <div className="absolute inset-0 z-0 opacity-30">
            <iframe
              className="w-full h-full object-cover"
              src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1&loop=1&playlist=${selectedVideo.id}&controls=0&showinfo=0&mute=${isMuted ? 1 : 0}&modestbranding=1`}
              title="Relaxation"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}

        {/* Timer principal */}
        <div className="relative z-10 text-center">
          {/* Animation cercle de progression */}
          <div className="relative w-64 h-64 mx-auto mb-8">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="128"
                cy="128"
                r="120"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="128"
                cy="128"
                r="120"
                stroke="url(#gradient)"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 120}`}
                strokeDashoffset={`${2 * Math.PI * 120 * (1 - progress / 100)}`}
                className="transition-all duration-1000"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="50%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#f472b6" />
                </linearGradient>
              </defs>
            </svg>
            
            {/* Contenu au centre */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <Clock className="w-8 h-8 mb-2 text-blue-300 animate-pulse" />
              <span className="text-5xl font-bold font-mono tracking-wider">
                {formatTime(remainingSeconds)}
              </span>
              <span className="text-sm opacity-70 mt-1">restant</span>
            </div>
          </div>

          {/* Message de détente */}
          <div className="space-y-2">
            <h2 className="text-2xl font-light">
              {infusion.is_paused ? (
                <span className="text-yellow-300">⏸️ Séance en pause</span>
              ) : progress < 25 ? (
                "Installez-vous confortablement..."
              ) : progress < 50 ? (
                "Respirez profondément et détendez-vous..."
              ) : progress < 75 ? (
                "Vous êtes à mi-chemin, continuez à vous relaxer..."
              ) : progress < 95 ? (
                "Presque terminé, encore un peu de patience..."
              ) : (
                "La séance se termine bientôt..."
              )}
            </h2>
            <p className="text-lg opacity-60">
              {Math.round(progress)}% complété
            </p>
          </div>

          {/* Vitamines du protocole */}
          {infusion.protocole?.vitamines && infusion.protocole.vitamines.length > 0 && (
            <div className="mt-8 max-w-md mx-auto">
              <p className="text-sm opacity-50 mb-2">Votre cocktail vitamines :</p>
              <div className="flex flex-wrap justify-center gap-2">
                {infusion.protocole.vitamines.slice(0, 6).map((vit, idx) => (
                  <span 
                    key={idx} 
                    className="px-3 py-1 bg-white/10 rounded-full text-sm backdrop-blur-sm"
                  >
                    {vit.length > 25 ? vit.substring(0, 25) + '...' : vit}
                  </span>
                ))}
                {infusion.protocole.vitamines.length > 6 && (
                  <span className="px-3 py-1 bg-white/10 rounded-full text-sm backdrop-blur-sm">
                    +{infusion.protocole.vitamines.length - 6} autres
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer avec sélection de vidéo */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/30 backdrop-blur-sm p-4 z-10">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs opacity-50 mb-2 text-center">Changer l'ambiance :</p>
          <div className="flex justify-center space-x-2 overflow-x-auto pb-2">
            {RELAXING_VIDEOS.map((video) => (
              <button
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                  selectedVideo.id === video.id
                    ? 'bg-white/30 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {video.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Animation de gouttes flottantes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/5"
            style={{
              width: `${Math.random() * 100 + 20}px`,
              height: `${Math.random() * 100 + 20}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-30px) rotate(180deg); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

export default PatientView;
